import { useMemo } from "react"

import { Button, Flex, Modal, Typography } from "antd"
import classNames from "classname"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router"

import Icon from "../../../../../Icon"
import { useBusinessAccountId } from "../../hooks/useBusinessAccountId"
import { useEnrollmentModal } from "./hooks/useEnrollmentModal"

import styles from "./EnrollmentModal.module.less"

const { Text } = Typography

const EnrollmentModal = () => {
  const navigate = useNavigate()
  const { isMobile } = useSelector((state) => state.nav)
  const { isAdmin } = useSelector((state) => state.auth)
  const { isOpened, close, enroll, loading: enrollmentLoading } = useEnrollmentModal()
  const enrollmentData = useSelector((state) => state.health.enrollmentData)
  const business_account = useBusinessAccountId()

  const onEnroll = async () => {
    try {
      await enroll({ business_account })
      close()
    } catch (error) {
      // Handle enrollment error if needed
      console.log("Enrollment failed:", error)
    }
  }

  const steps = useMemo(() => {
    const stripeStep = {
      title: "Stripe integration",
      description: "Connect Stripe account to enable payment monitoring.",
      isPassed: enrollmentData?.steps?.is_stripe_connected,
      onClick: () => {
        navigate("/integrations")
        close()
      }
    }

    const rdrStep = {
      title: "RDR enrollment",
      description: "Set up RDR to auto-resolve chargebacks.",
      isPassed: enrollmentData?.steps?.is_rdr_active,
      onClick: () => {
        if (isAdmin) {
          navigate("/business_settings/enrollment")
        } else {
          navigate("/configure/enrollment?tab=RDR")
        }
        close()
      }
    }

    const enrollStep = {
      title: "Enroll in VAMP",
      description: "Join Visa’s fraud and dispute monitoring program.",
      isDisabled: !rdrStep.isPassed || !stripeStep.isPassed,
      isPassed: enrollmentData?.steps?.enrolled,
      onClick: onEnroll,
      loading: enrollmentLoading
    }
    return [stripeStep, rdrStep, enrollStep]
  }, [enrollmentData, navigate, enrollmentLoading])

  const totalSteps = steps.length
  const completedSteps = steps.filter((step) => step.isPassed).length

  return (
    <Modal
      open={isOpened}
      onCancel={close}
      onClose={close}
      getContainer={document.querySelector(".main-layout")}
      centered={!isMobile}
      closeIcon={<Icon type="cross_small" />}
      footer={null}
      className={styles.modal}
      classNames={{ content: "clear-bg" }}
      width="100%"
    >
      <Flex vertical gap={16}>
        <Flex vertical gap={8}>
          <Text className={styles.title}>Requirements to access this page</Text>
          <Text className={styles.modalDesc} type="secondary">
            You need to complete the following steps to unlock full access:
          </Text>
        </Flex>
        <Flex vertical gap={12}>
          <Flex gap={12} align="center">
            <Text type="secondary">Configure</Text>
            <div className={styles.horizontalLine} />
            <div className={styles.progressTag}>
              {completedSteps} <span className={styles.slash}>/</span> {totalSteps} completed
            </div>
          </Flex>
          <div>
            {steps.map((step, index) => (
              <EnrollmentStep
                key={index}
                index={index + 1}
                title={step.title}
                description={step.description}
                isDisabled={step.isDisabled}
                isPassed={step.isPassed}
                withDivider={index !== steps.length - 1}
                onClick={step?.onClick}
                loading={step?.loading}
              />
            ))}
          </div>
        </Flex>
      </Flex>
    </Modal>
  )
}

const EnrollmentStep = ({
  index,
  title,
  description,
  onClick,
  isPassed,
  isDisabled,
  withDivider,
  loading
}) => {
  return (
    <Flex align="center" gap={14} className={styles.stepBlock}>
      <div
        flex="0 0"
        className={classNames(styles.stepIndex, {
          [styles.passed]: isPassed
        })}
      >
        {isPassed ? <Icon type="check" /> : index}
      </div>
      <Flex
        gap={12}
        flex="1"
        align="center"
        className={classNames(styles.stepContent, {
          [styles.divider]: withDivider
        })}
      >
        <Flex gap={2} vertical flex="1">
          <Text strong className={styles.stepTitle}>
            {title}
          </Text>
          <Text type="secondary" className={styles.stepDescription}>
            {description}
          </Text>
        </Flex>
        {!isPassed && (
          <Button
            onClick={onClick}
            disabled={isDisabled}
            className={styles.stepBtn}
            loading={loading}
          >
            Continue
          </Button>
        )}
      </Flex>
    </Flex>
  )
}

export default EnrollmentModal
